import { useEffect, useState } from 'react';
import { fetchOroborosPortfolioDashboard } from '../api/portfolio';
import {
  computeTradeWindowStats,
  fetchOroborosTradeHistory,
  formatLandingStats,
} from '../api/trades';
import { refreshOroborosSession } from '../api/session';
import { hasArtificeSessionMarker } from '../../auth/artificeContinue';
import { hasTerminalSessionHint } from '../../utils/terminalSessionRefresh';

export type LandingStat = {
  value: string;
  label: string;
  positive?: boolean;
};

/**
 * Landing strip: authenticated → real account equity / realized windows;
 * signed-out → hidden (no public tape).
 */
export function useOroborosLandingStats() {
  const [stats, setStats] = useState<LandingStat[]>([]);
  const [mode, setMode] = useState<'loading' | 'account' | 'guest'>('loading');
  const [guestSessionProbeDone, setGuestSessionProbeDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const guestRefreshPaused =
      guestSessionProbeDone &&
      mode === 'guest' &&
      !hasTerminalSessionHint() &&
      !hasArtificeSessionMarker();

    const load = async () => {
      if (guestRefreshPaused) {
        return;
      }
      const session = await refreshOroborosSession({ skipIfKnownAbsent: true });
      if (cancelled) return;

      if (session.success) {
        const [dashRes, histRes] = await Promise.all([
          fetchOroborosPortfolioDashboard(),
          fetchOroborosTradeHistory(90),
        ]);
        if (cancelled) return;
        if (dashRes.success && dashRes.dashboard) {
          const windows = histRes.success
            ? computeTradeWindowStats(histRes.trades as any)
            : null;
          setStats(
            formatLandingStats({
              equity: dashRes.dashboard.totalPortfolioValue,
              unrealized: dashRes.dashboard.totalUnrealizedPnL,
              windows,
            }),
          );
          setMode('account');
          return;
        }
        // Session is live — keep the last account strip instead of flipping to guest.
        setMode('account');
        return;
      }

      if (!cancelled) {
        setStats((prev) => (prev.length && session.status === 0 ? prev : []));
        setMode((prev) => (prev === 'account' && session.status === 0 ? prev : 'guest'));
        if (
          !session.success &&
          session.status === 401 &&
          !hasTerminalSessionHint() &&
          !hasArtificeSessionMarker()
        ) {
          setGuestSessionProbeDone(true);
        }
      }
    };

    void load();
    const id = guestRefreshPaused ? null : window.setInterval(() => void load(), 30_000);
    return () => {
      cancelled = true;
      if (id != null) window.clearInterval(id);
    };
  }, [guestSessionProbeDone, mode]);

  return { stats, mode };
}
